/**
 * Générateur — Automatisme : relations entre 1, 1/10, 1/100 et 1/1000
 * Attendu ID : 1005 | Niveau : 6ème | Domaine : NC
 * Rubrique : AUTOMATISME (programme 2025) — restitution en rituel court.
 * Labels (cartes exerciseur) :
 *   - Relations entre 1, 1/10, 1/100 et 1/1000
 *
 * ── Plan de cartes validé (2026-08-19) ─────────────────────────────────────
 * UNE carte, 5 formats rituels. Domaine FINI modélisé par construction :
 * une égalité est 1/10^p = 10^q / 10^(p+q)  (forme F, fractionnaire)
 *            ou  1/10^p = 10^q × 1/10^(p+q) (forme M, multiplicative)
 * avec p ∈ {0,1,2}, q ≥ 1, p+q ≤ 3. Variantes : forme, sens d'écriture,
 * position du trou (num/den/fac). INVARIANTS :
 *   - UN SEUL trou par égalité (équation linéaire → réponse UNIQUE ; le cas
 *     « 5 = … + … » à solutions multiples est exclu par design) ;
 *   - jamais de trou sur « 1 » : les réponses sont toujours 10, 100 ou 1000 ;
 *   - chaque égalité émise est RE-VÉRIFIÉE numériquement (throw si fausse) ;
 *   - drag : les réponses d'un tirage sont des valeurs DISTINCTES (permutation
 *     de {10,100,1000}) → aucune tuile n'est juste hors de son trou ;
 *   - QCM : les choix sont TOUJOURS les 4 puissances {1,10,100,1000} —
 *     distinction par nature, zéro bouche-trou (cf. REGLES_DISTRACTEURS_QCM) ;
 *   - V/F : les lignes fausses sont un décalage ×10 EXACTEMENT (l'erreur
 *     cible de l'attendu), re-vérifiées fausses numériquement ;
 *   - milliers : affichage 1\,000 (espace fine), mots de trous et choix en
 *     brut « 1000 » (la tolérance numérique du scoring accepte « 1 000 »).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeClasser, filtrerDistracteurs, tex,
} from '../../../utils';

const ATTENDU_ID = 1005;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Relations entre 1, 1/10, 1/100 et 1/1000';

// ─── Le domaine ────────────────────────────────────────────────────────────

/** Puissances de dix du domaine (index = exposant). */
const P = [1, 10, 100, 1000];

/** Affichage d'une puissance : espace fine des milliers, jamais de virgule. */
const fmtTex = (n) => (n === 1000 ? '1\\,000' : String(n));

/**
 * Énumération complète des égalités trouables.
 * item = { forme: 'F'|'M', p, q, sens: 0|1, trou: 'num'|'den'|'fac' }
 *   forme F : 1/10^p = 10^q / 10^(p+q)      trous : num (10^q), den (10^(p+q))
 *   forme M : 1/10^p = 10^q × 1/10^(p+q)    trous : fac (10^q), den (10^(p+q))
 *   sens 1 : l'égalité est écrite membre droit d'abord (10/100 = 1/10).
 * Jamais de trou sur le membre gauche (le « 1 » ou 1/10^p de référence).
 */
const ITEMS = [];
for (let p = 0; p <= 2; p++) {
    for (let q = 1; p + q <= 3; q++) {
        for (const forme of ['F', 'M']) {
            const trous = forme === 'F' ? ['num', 'den'] : ['fac', 'den'];
            for (const trou of trous) {
                for (const sens of [0, 1]) {
                    ITEMS.push({ forme, p, q, sens, trou });
                }
            }
        }
    }
}

/** Valeur attendue dans le trou d'un item. */
const reponseDe = (item) => (item.trou === 'den' ? P[item.p + item.q] : P[item.q]);

/** Clé de la relation mathématique sous-jacente (sans trou ni sens). */
const cleRelation = (item) => `${item.forme}|${item.p}|${item.q}`;

/**
 * Membre gauche (la référence, jamais trouée) en LaTeX.
 * p = 0 → « 1 » ; sinon 1/10^p en \dfrac.
 */
const texGauche = (item) =>
    (item.p === 0 ? '1' : `\\dfrac{1}{${fmtTex(P[item.p])}}`);

/**
 * Membre droit en LaTeX. `slot` remplace la valeur trouée :
 *   - null : tout affiché (ligne pleine, V/F) ;
 *   - 'trou' : le marqueur {{valeur}} (texte à trous — triple accolade
 *     automatique quand le slot est DANS un \dfrac) ;
 *   - 'question' : « ? » (QCM).
 */
const texDroit = (item, slot = null) => {
    const rep = reponseDe(item);
    const marque = slot === 'trou' ? `{{${rep}}}` : slot === 'question' ? '?' : null;
    const num = item.trou !== 'den' && marque !== null ? marque : fmtTex(P[item.q]);
    const den = item.trou === 'den' && marque !== null ? marque : fmtTex(P[item.p + item.q]);
    if (item.forme === 'F') return `\\dfrac{${num}}{${den}}`;
    return `${num} \\times \\dfrac{1}{${den}}`;
};

/** L'égalité complète (sans les $), sens appliqué. */
const texEgalite = (item, slot = null) => {
    const g = texGauche(item);
    const d = texDroit(item, slot);
    return item.sens === 0 ? `${g} = ${d}` : `${d} = ${g}`;
};

/**
 * AUTO-VÉRIFICATION : valeur numérique des deux membres, égalité exigée.
 * Filet contre toute évolution qui casserait la construction : on préfère un
 * générateur qui refuse d'émettre à un exercice mathématiquement faux.
 */
const verifierItem = (item) => {
    const gauche = 1 / P[item.p];
    const droit = item.forme === 'F'
        ? P[item.q] / P[item.p + item.q]
        : P[item.q] * (1 / P[item.p + item.q]);
    if (Math.abs(gauche - droit) > 1e-12) {
        throw new Error(`gen_1005 : égalité fausse par construction (${cleRelation(item)})`);
    }
    return item;
};

/**
 * Tire `n` items à trous dont les RELATIONS sous-jacentes sont distinctes
 * (deux positions de trou d'une même relation se dévoileraient l'une l'autre),
 * avec éventuellement une contrainte sur la réponse attendue.
 * `maxAvecUn` plafonne les relations dont la référence est « 1 » (p = 0) :
 * elles affichent forcément l'autre moitié de la paire (1 = n/n), donc un
 * tirage qui n'aurait qu'elles serait trop donné — décision revue 2026-08-19,
 * plafond 2/4 en saisie pour garantir la moitié du tirage sur les relations
 * 1/10 ↔ 1/100 ↔ 1/1000 (les plus discriminantes).
 */
const tirerItems = (n, contrainteReponse = null, maxAvecUn = null) => {
    const candidats = shuffle(
        ITEMS.filter((it) => (contrainteReponse === null || reponseDe(it) === contrainteReponse)),
    );
    const pris = [];
    const relationsVues = new Set();
    let avecUn = 0;
    for (const it of candidats) {
        if (relationsVues.has(cleRelation(it))) continue;
        if (maxAvecUn !== null && it.p === 0 && avecUn >= maxAvecUn) continue;
        relationsVues.add(cleRelation(it));
        if (it.p === 0) avecUn++;
        pris.push(verifierItem(it));
        if (pris.length === n) break;
    }
    return pris;
};

// ─── Format 1 : texte à trous SAISIE (la série flash) ─────────────────────

const genRelationsTrouSaisie = () => {
    // Au moins 2 valeurs de réponse distinctes sur les 4 trous (sinon taper
    // « 1000 » partout réussirait sans lire) — boucle bornée, repli assumé.
    // Plafond 2/4 sur les relations « avec 1 » (cf. tirerItems).
    let items = tirerItems(4, null, 2);
    for (let essai = 0; essai < 30; essai++) {
        if (new Set(items.map(reponseDe)).size >= 2) break;
        items = tirerItems(4, null, 2);
    }
    const phrase = items.map((it) => `$${texEgalite(it, 'trou')}$`).join('\n');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 2 : texte à trous DRAG ────────────────────────────────────────

const genRelationsTrouDrag = () => {
    // Réponses toutes DISTINCTES (une égalité par valeur de {10, 100, 1000})
    // ET relations toutes distinctes, en UN tirage : deux trous d'une même
    // relation se dévoileraient l'un l'autre. Plafond 2/3 sur les relations
    // « avec 1 » (p = 0), même décision que la saisie (revue 2026-08-19) :
    // un tirage 3/3 se résolvait par appariement visuel des paires n/n.
    // Le pool garantit une solution ; repli déterministe conforme (2 avec-1).
    let items = [];
    let avecUn = 0;
    for (const it of shuffle(ITEMS)) {
        if (items.some((x) => cleRelation(x) === cleRelation(it)
            || reponseDe(x) === reponseDe(it))) continue;
        if (it.p === 0 && avecUn >= 2) continue;
        if (it.p === 0) avecUn++;
        items.push(verifierItem(it));
        if (items.length === 3) break;
    }
    if (items.length < 3) {
        items = [
            { forme: 'F', p: 1, q: 1, sens: 0, trou: 'num' },   // 1/10 = {{10}}/100
            { forme: 'F', p: 0, q: 2, sens: 0, trou: 'den' },   // 1 = 100/{{100}}… réponse 100
            { forme: 'M', p: 0, q: 3, sens: 0, trou: 'fac' },   // 1 = {{1000}} × 1/1000
        ].map(verifierItem);
    }
    const phrase = items.map((it) => `$${texEgalite(it, 'trou')}$`).join('\n');
    // Tuiles pièges : « 1 » (aucune transformation — LA misconception) et
    // « 10000 » (décalage ×10 au-delà du domaine). Aucune ne peut égaler un
    // mot attendu ({10,100,1000}), filtrerDistracteurs reste le filet.
    const distracteurs = filtrerDistracteurs(phrase, ['1', '10000']);
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité avec les bonnes étiquettes.</b></p>',
        { ...opts(L1), mode: 'drag', distracteurs },
    );
};

// ─── Format 3 : QCM ───────────────────────────────────────────────────────

/**
 * Contrat distracteurs (cf. REGLES_DISTRACTEURS_QCM) : les choix sont
 * TOUJOURS les 4 puissances {1, 10, 100, 1000}. La bonne réponse en est une
 * (jamais 1, le « 1 » n'est jamais troué) ; chaque distracteur incarne une
 * erreur nommée relative à elle.
 */
const genRelationsQcm = () => {
    const item = tirerItems(1)[0];
    const rep = reponseDe(item);
    const texte = '<p><b>Quel nombre remplace le « ? » ?</b></p>'
        + `<p>${tex(texEgalite(item, 'question'))}</p>`;
    const choix = P.map(String);
    const payload = makeQcm(choix, String(rep), texte, opts(L1), false);
    payload._meta.params = {
        ...item,
        reponse: rep,
        distracteurs: P.filter((v) => v !== rep).map((v) => ({
            valeur: v,
            code: v === 1 ? 'aucune_transformation' : (v > rep ? 'decalage_x10_haut' : 'decalage_x10_bas'),
        })),
    };
    return payload;
};

// ─── Format 4 : tableau Vrai/Faux ─────────────────────────────────────────

/**
 * Ligne fausse = une égalité vraie dont UNE valeur du membre droit est
 * décalée d'un facteur 10 (l'erreur cible de l'attendu). Le décalage reste
 * dans le domaine [1, 1000] ; re-vérifiée FAUSSE numériquement.
 */
const fausserItem = (item) => {
    const rep = reponseDe(item);
    const direction = rep >= 1000 ? 0.1 : rep <= 1 ? 10 : randomChoice([10, 0.1]);
    const valeurFausse = Math.round(rep * direction);
    const droitFaux = (() => {
        const num = item.trou !== 'den' ? fmtTex(valeurFausse) : fmtTex(P[item.q]);
        const den = item.trou === 'den' ? fmtTex(valeurFausse) : fmtTex(P[item.p + item.q]);
        if (item.forme === 'F') return `\\dfrac{${num}}{${den}}`;
        return `${num} \\times \\dfrac{1}{${den}}`;
    })();
    const g = texGauche(item);
    const ligne = item.sens === 0 ? `${g} = ${droitFaux}` : `${droitFaux} = ${g}`;
    // Vérification : l'égalité affichée est bien FAUSSE.
    const numVal = item.trou !== 'den' ? valeurFausse : P[item.q];
    const denVal = item.trou === 'den' ? valeurFausse : P[item.p + item.q];
    const droitVal = item.forme === 'F' ? numVal / denVal : numVal * (1 / denVal);
    if (Math.abs(1 / P[item.p] - droitVal) < 1e-12) {
        throw new Error('gen_1005 : la ligne censée être fausse est vraie');
    }
    return ligne;
};

const genRelationsVraiFaux = () => {
    // Deux lignes FAUSSÉES issues de relations différentes peuvent entrer en
    // collision (« 1/10 = 10/1000 » est atteignable depuis F|1|1 dén décalé
    // ET F|1|2 num décalé) → makeVraiFaux dédoublonnerait à 3 lignes. Boucle
    // bornée jusqu'à 4 lignes distinctes ; repli : makeVraiFaux dédoublonne
    // proprement (3 lignes distinctes, minimum du format respecté).
    // NOTE (arbitrage prof 2026-08-19) : deux lignes portant la MÊME égalité
    // écrite dans les deux sens (« 1 = 1000 × 1/100 » et « 1000 × 1/100 = 1 »)
    // sont ACCEPTÉES volontairement : elles testent la symétrie de l'égalité
    // (un élève qui répond différemment aux deux révèle l'incompréhension).
    // Ne pas « corriger » par une canonisation des membres.
    let lignes = [];
    let etiquettes = [];
    for (let essai = 0; essai < 30; essai++) {
        // Plafond 2/4 sur les relations « avec 1 » (même décision que la
        // saisie et le drag) : couverture garantie des relations fines.
        const items = tirerItems(4, null, 2);
        const nbVraies = randInt(1, 3);
        etiquettes = shuffle([
            ...Array(nbVraies).fill(0),
            ...Array(4 - nbVraies).fill(1),
        ]);
        lignes = items.map((it, i) => (
            etiquettes[i] === 0 ? `$${texEgalite(it)}$` : `$${fausserItem(it)}$`
        ));
        if (new Set(lignes).size === 4) break;
    }
    return makeVraiFaux(
        lignes,
        etiquettes,
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 5 : classer ───────────────────────────────────────────────────

/** Écritures classables, par valeur cible. Chaque écriture n'appartient
 *  qu'à UNE catégorie (les valeurs 1, 1/10, 1/100 sont distinctes). */
const ECRITURES = {
    un: [
        '$\\dfrac{10}{10}$', '$\\dfrac{100}{100}$', '$\\dfrac{1\\,000}{1\\,000}$',
        '$10 \\times \\dfrac{1}{10}$', '$100 \\times \\dfrac{1}{100}$', '$1\\,000 \\times \\dfrac{1}{1\\,000}$',
    ],
    dixieme: [
        '$\\dfrac{10}{100}$', '$\\dfrac{100}{1\\,000}$',
        '$10 \\times \\dfrac{1}{100}$', '$100 \\times \\dfrac{1}{1\\,000}$',
    ],
    centieme: [
        '$\\dfrac{10}{1\\,000}$', '$10 \\times \\dfrac{1}{1\\,000}$',
    ],
};

const genRelationsClasser = () => {
    const categories = [
        { name: 'Égal à 1', items: shuffle(ECRITURES.un).slice(0, randInt(2, 3)) },
        { name: 'Égal à un dixième', items: shuffle(ECRITURES.dixieme).slice(0, randInt(2, 3)) },
        { name: 'Égal à un centième', items: [...ECRITURES.centieme] },
    ];
    return makeClasser(
        categories,
        '<p><b>Classe chaque écriture selon le nombre qu\'elle représente.</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue, format QCM) ────────────────

/**
 * Chaque erreur est une MISCONCEPTION NOMMÉE d'élève, jamais du bruit.
 * Arbitrage prof (décideur didactique), 2026-08-19.
 */
export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'les 4 choix sont TOUJOURS les 4 puissances {1, 10, 100, 1000} : distinction garantie par nature, aucun bouche-trou possible',
        "la bonne réponse n'est jamais 1 (le « 1 » n'est jamais troué) : le choix « 1 » est donc toujours un distracteur disponible",
        'chaque distracteur porte son code dans _meta.params.distracteurs (futur feedback pédagogique nommant l\'erreur)',
        "SUPPRIMÉ par arbitrage — ne pas réintroduire : copie_visuelle (recopier le dénominateur voisin, ex. répondre 100 à 1/10 = ?/100) : erreur artificielle, remplacée par aucune_transformation (arbitrage utilisateur 2026-08-19)",
    ],
    relations_qcm_unique: {
        support: 'une égalité du domaine (forme fraction ou multiplicative), trou affiché « ? »',
        fixes: {
            aucune_transformation: "le choix « 1 » : l'élève recopie le numérateur tel quel (croit que 1/10 = 1/100) ou pense que « × 1 » convient — LA misconception racine de l'attendu",
            decalage_x10_haut: 'la puissance un cran (ou plus) au-dessus de la bonne : un zéro de trop',
            decalage_x10_bas: 'la puissance un cran (ou plus) en dessous : un zéro de moins',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    relations_texte_trou_saisie: genRelationsTrouSaisie,
    relations_texte_trou_drag: genRelationsTrouDrag,
    relations_qcm_unique: genRelationsQcm,
    relations_tableau_vf: genRelationsVraiFaux,
    relations_classer: genRelationsClasser,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    tableau_vf: 'Vrai/Faux', classer: 'Classer',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'relations', label: L1, formatsList: buildFormats('relations') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
